/**
 * Capability readiness — what each served process reports about its own capabilities once,
 * at boot, after every module has registered.
 *
 * PLATFORM-scoped and keyed `(capability, <capability name>)`: one missing variable that web
 * and worker both read is ONE problem and one row, and `rawPayload.reportedBy` names the
 * process that reported it last. Two codes, because the two states call for different
 * responses: an unavailable capability refuses its callers, a degraded one serves them less.
 *
 * Every boot re-states the whole picture: a ready capability resolves both rows, and a
 * not-ready one reports its own code and resolves the other — so a move from degraded to
 * unavailable OPENS the unavailable row and pages, and a fix resolves on the next boot that
 * sees it. Why the last process to boot may decide alone is in
 * `apps/backend/src/_common/capabilities/alerting/capability-issue.ts`.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

const READ_THE_PAYLOAD =
  "Read `rawPayload`: `absent` lists the environment variables the process named in `reportedBy` looked up and found no value for — set those in the deployment's environment group and restart. " +
  "`unresolved` lists requirements that are patterns rather than names: set the variable each one names first, and its real name follows. " +
  "When both are empty, the readiness is not explained by a missing variable; `reason` is the capability's own statement of why. " +
  "Readiness is read at process start, so no CI gate can see it and a fix shows here only after the next boot.";

export const CAPABILITY_ISSUES = {
  /**
   * A capability is OFF on a served process: its callers are refused at its own boundary.
   *
   * ## Pages
   *
   * This replaced a crash. A missing variable used to kill every pod, which nobody could miss;
   * it now boots and reports here instead, and a capability off with nobody told is an outage
   * nobody hears about.
   */
  CAPABILITY_UNAVAILABLE: {
    category: ISSUE_CATEGORY.CONFIGURATION,
    message: "A capability is unavailable on a served process — its callers are refused",
    severity: "high",
    page: true,
    remedy: READ_THE_PAYLOAD
  },
  /**
   * A capability runs with reduced function: callers should expect LESS, not nothing.
   *
   * ## Does not page
   *
   * The process and its callers are working. Measured on production 2026-09-17,
   * `voice-synthesis` degraded meant one preview button failed while the send path and a live
   * fallback were intact — real, worth a row, not worth interrupting anyone.
   */
  CAPABILITY_DEGRADED: {
    category: ISSUE_CATEGORY.CONFIGURATION,
    message:
      "A capability is running degraded on a served process — its callers get reduced function",
    severity: "medium",
    remedy: READ_THE_PAYLOAD
  }
} as const satisfies Record<string, IssueDefinition>;
